/**
 * PB Offers Drawer — cart-like slide-in panel for consolidated offers.
 * Open via window.PBOffersDrawer / offers:open / [data-open-offers-drawer].
 */
(function () {
  const DRAWER_SELECTOR = '[data-pb-offers-drawer="primary"]';
  const BACKDROP_CLASS = 'pb-offers-backdrop';
  const OPEN_CLASS = 'pb-offers-open';
  const DRAWER_TRANSITION_MS = 380;

  function getDrawer() {
    return document.querySelector(DRAWER_SELECTOR);
  }

  function init() {
    if (window.__pbOffersDrawerInit) return;
    window.__pbOffersDrawerInit = true;

    const drawer = getDrawer();
    if (!drawer) return;

    let backdrop = document.querySelector(`.${BACKDROP_CLASS}`);
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = BACKDROP_CLASS;
      document.body.appendChild(backdrop);
    }

    let scrollY = 0;
    let closeTimer = 0;
    let lastFocus = null;

    // <html> only — see cart-drawer-toggle.js: `overflow: hidden` on <body>
    // turns the body into a scroll container and every sticky element on the
    // page jumps to the body's top instead of staying put on the viewport.
    function lockScroll() {
      scrollY = window.scrollY;
      document.documentElement.style.overflow = 'hidden';
    }

    function unlockScroll() {
      document.documentElement.style.overflow = '';
      window.scrollTo(0, scrollY);
    }

    function openDrawer() {
      if (drawer.classList.contains('is-open')) return;

      window.PBUnifiedNavOverlay?.closeAll();
      window.closeSearchPopup?.();
      window.PBCartDrawer?.close?.();

      const membershipModals = document.querySelectorAll('[id^="pb-ap-modal-"].is-open');
      for (const modal of membershipModals) {
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        window.PBOverlayStack?.pop(modal);
      }

      window.clearTimeout(closeTimer);
      lastFocus = document.activeElement;
      scrollY = window.scrollY;
      lockScroll();

      if (window.PBOverlayStack) {
        window.PBOverlayStack.push(backdrop);
        window.PBOverlayStack.push(drawer);
      }

      document.documentElement.classList.add(OPEN_CLASS);
      document.body.classList.add(OPEN_CLASS);
      drawer.setAttribute('aria-hidden', 'false');

      requestAnimationFrame(() => {
        void drawer.offsetWidth;
        backdrop.classList.add('is-visible');
        drawer.classList.add('is-open');
        drawer.querySelector('.pb-offers-drawer__close')?.focus();
      });
    }

    function finishClose() {
      if (window.PBOverlayStack) {
        window.PBOverlayStack.pop(drawer);
        window.PBOverlayStack.pop(backdrop);
      }
      unlockScroll();
      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      }
      lastFocus = null;
    }

    function closeDrawer() {
      if (!drawer.classList.contains('is-open')) return;

      drawer.classList.remove('is-open');
      backdrop.classList.remove('is-visible');
      drawer.setAttribute('aria-hidden', 'true');

      let finished = false;
      const complete = () => {
        if (finished) return;
        finished = true;
        drawer.removeEventListener('transitionend', onTransitionEnd);
        window.clearTimeout(closeTimer);
        document.documentElement.classList.remove(OPEN_CLASS);
        document.body.classList.remove(OPEN_CLASS);
        finishClose();
      };

      const onTransitionEnd = (event) => {
        if (event.target !== drawer || event.propertyName !== 'right') return;
        complete();
      };

      drawer.addEventListener('transitionend', onTransitionEnd);
      closeTimer = window.setTimeout(complete, DRAWER_TRANSITION_MS);
    }

    function toggleDrawer() {
      if (drawer.classList.contains('is-open')) {
        closeDrawer();
      } else {
        openDrawer();
      }
    }

    window.PBOffersDrawer = { open: openDrawer, close: closeDrawer, toggle: toggleDrawer };

    drawer.querySelector('.pb-offers-drawer__close')?.addEventListener('click', closeDrawer);
    backdrop.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeDrawer();
    });

    document.addEventListener('offers:open', openDrawer);
    document.addEventListener('offers:close', closeDrawer);
    document.addEventListener('offers:toggle', toggleDrawer);

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest?.('[data-open-offers-drawer]');
      if (!trigger) return;
      event.preventDefault();
      openDrawer();
    });

    // Accordion rows — smooth open/close via CSS grid + aria-expanded
    drawer.addEventListener('click', (event) => {
      const toggle = event.target.closest?.('[data-offers-accordion-toggle]');
      if (!toggle) return;

      event.preventDefault();
      const item = toggle.closest('[data-offers-item]');
      if (!item || item.dataset.expandable !== 'true') return;

      const expanded = item.getAttribute('aria-expanded') === 'true';
      const next = expanded ? 'false' : 'true';
      item.setAttribute('aria-expanded', next);
      toggle.setAttribute('aria-expanded', next);
      const panel = item.querySelector('[data-offers-accordion]');
      if (panel) {
        panel.setAttribute('aria-hidden', next === 'true' ? 'false' : 'true');
      }
    });

    sortAllChildLinkLists(drawer);
  }

  /**
   * Place items with data-offers-seq at that exact 1-based index.
   * Remaining slots fill with unsequenced items (products then collections
   * customizer order via data-offers-default-index).
   */
  function sortChildLinkList(list) {
    if (!list || list.dataset.offersSorted === 'true') return;

    const items = Array.from(list.children);
    if (items.length < 2) {
      list.dataset.offersSorted = 'true';
      return;
    }

    const n = items.length;
    const slots = new Array(n).fill(null);
    const unsequenced = [];

    for (const el of items) {
      const raw = el.getAttribute('data-offers-seq');
      const seq = raw != null && raw !== '' ? Number(raw) : NaN;
      if (Number.isFinite(seq) && seq > 0) {
        let idx = Math.min(Math.max(Math.floor(seq), 1), n) - 1;
        if (slots[idx] != null) {
          // Slot taken — first free from that index (wrap)
          let placed = false;
          for (let step = 0; step < n; step += 1) {
            const j = (idx + step) % n;
            if (slots[j] == null) {
              slots[j] = el;
              placed = true;
              break;
            }
          }
          if (!placed) unsequenced.push(el);
        } else {
          slots[idx] = el;
        }
      } else {
        unsequenced.push(el);
      }
    }

    unsequenced.sort((a, b) => {
      const ai = Number(a.getAttribute('data-offers-default-index')) || 0;
      const bi = Number(b.getAttribute('data-offers-default-index')) || 0;
      return ai - bi;
    });

    let u = 0;
    for (let i = 0; i < n; i += 1) {
      if (slots[i] == null) {
        slots[i] = unsequenced[u];
        u += 1;
      }
    }

    for (const el of slots) {
      if (el) list.appendChild(el);
    }

    list.dataset.offersSorted = 'true';
  }

  function sortAllChildLinkLists(root) {
    if (!root) return;
    for (const list of root.querySelectorAll('[data-offers-child-links]')) {
      sortChildLinkList(list);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
